import type {
  UpdateProgramMarketplaceConfigArgs,
  UnarchiveProgramByIdArgs,
  FetchSignedUploadUrlArgs,
  ArchiveProgramByIdArgs,
  FetchProgramsArgs,
  CreateProgramArgs,
  FetchProgramById,
  EditProgramArgs,
} from './types/programs.types';
import type {
  ApiSignedUploadUrl,
  UiSignedUploadUrl,
} from './types/files.types';
import type { ApiProgram } from '../schemas/programs.schema';
import type { BaseApiResponse } from './types/api.types';

import {
  createProgramApiArgs,
  mapProgramFromApi,
} from './mappers/programs.mappers';
import { postWithPagination, destroy, patch, post, get } from './axios';
import { DEFAULT_CURRENCY } from '../config/constants';

export const fetchSignedUploadUrl = async ({
  mediaType,
  organizationId,
  target,
}: FetchSignedUploadUrlArgs) => {
  const response = await get<BaseApiResponse<ApiSignedUploadUrl>>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/programs/signed-upload-url?mediaType=${mediaType}${target ? `&target=${target}` : ''}`,
  );

  return response?.data as UiSignedUploadUrl;
};

export const createProgram = async ({
  organizationId,
  ...args
}: CreateProgramArgs) => {
  const response = await post<ApiProgram>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/programs`,
    createProgramApiArgs(args),
  );
  if (!response?.data) return null;
  return mapProgramFromApi(response?.data, args.locale);
};

export const editProgramById = async ({
  id,
  organizationId,
  ...args
}: EditProgramArgs) => {
  const response = await patch<ApiProgram>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/programs/${id}`,
    createProgramApiArgs(args),
  );
  if (!response?.data) return null;
  return mapProgramFromApi(response?.data, args.locale);
};

export const fetchProgramById = async ({
  id,
  locale,
  organizationId,
}: FetchProgramById) => {
  const url = `/v2${organizationId ? `/organizations/${organizationId}` : ''}/programs/${id}`;

  const response = await get<BaseApiResponse<ApiProgram>>(url);
  if (!response?.data) return null;
  return mapProgramFromApi(response?.data, locale);
};

export const fetchPrograms = async ({
  contentFilters,
  locale,
  organizationId,
  tableFilters,
}: FetchProgramsArgs) => {
  const { search, ...restTableFilters } = tableFilters;
  const {
    authorIds,
    equipmentTags,
    isDeleted,
    isHome,
    isMarketplace,
    organizationIds,
    selectedUserIds,
    sources,
    tags,
  } = contentFilters || {};

  const params = {
    ...restTableFilters,
    status: 'active',
    ...(search ? { search } : {}),
    authorIds,
    equipmentTagIds: equipmentTags || undefined,
    isDeleted,
    isHome,
    isMarketplace,
    organizationIds,
    selectedUserIds,
    sources,
    tagIds: tags || undefined,
  };
  const response = await postWithPagination<ApiProgram[]>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/programs/list`,
    params,
  );
  if (!response?.data) return null;
  return {
    ...response,
    data: response?.data.map((program) => mapProgramFromApi(program, locale)),
  };
};

export const updateProgramMarketplaceConfig = async ({
  id,
  currency = DEFAULT_CURRENCY,
  enabled,
  organizationId,
  price,
}: UpdateProgramMarketplaceConfigArgs) => {
  await patch(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/programs/${id}/marketplace-config`,
    {
      configuration: {
        marketplace: { currency, enabled, price },
      },
    },
  );
};

export const archiveProgramById = async ({
  id,
  organizationId,
}: ArchiveProgramByIdArgs) => {
  await destroy(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/programs/${id}`,
  );
  return true;
};

export const unarchiveProgramById = async ({
  id,
  organizationId,
}: UnarchiveProgramByIdArgs) => {
  await patch(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/programs/${id}/restore`,
  );
  return true;
};
